/**
 * Approvals data + mutation hooks — bound to the EDR-connector routes
 * at `/api/v1/connectors/edr/lighthouse/approvals*`. Missing routes
 * (404/405/403) are coerced into empty data so the page renders a
 * graceful empty state before the backend lands.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { edrGet, edrPost, EdrApiError } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type {
  ApprovalConditions,
  ApprovalCounts,
  ApprovalDetailResponse,
  ApprovalHistoryResponse,
  ApprovalListResponse,
  ApprovalState,
  ApprovalSweepResponse,
} from "@/types/lighthouseApprovals";

const BASE = "/api/v1/connectors/edr/lighthouse/approvals";
const STALE_MS = 15_000;

const EMPTY_COUNTS: ApprovalCounts = {
  pending: 0,
  approved: 0,
  denied: 0,
  infoRequested: 0,
  expired: 0,
};

function isMissingRoute(err: unknown): boolean {
  const status = err instanceof EdrApiError ? err.status : 0;
  return status === 404 || status === 405 || status === 403;
}

// ---------------- Queries ----------------

export function useApprovalsSummary() {
  const enabled = useConnectorEnabled("lighthouse");
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "approvals", "summary"],
    staleTime: STALE_MS,
    queryFn: async (): Promise<ApprovalCounts> => {
      try {
        const data = await edrGet<Partial<ApprovalCounts>>(`${BASE}/summary`);
        return { ...EMPTY_COUNTS, ...(data ?? {}) };
      } catch (err) {
        if (isMissingRoute(err)) return EMPTY_COUNTS;
        throw err;
      }
    },
  });
}

export function useApprovalsList(states: ApprovalState[], limit = 200) {
  const enabled = useConnectorEnabled("lighthouse");
  const stateParam = states.join(",");
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "approvals", "list", stateParam, limit],
    staleTime: STALE_MS,
    queryFn: async (): Promise<ApprovalListResponse> => {
      try {
        const data = await edrGet<ApprovalListResponse>(BASE, {
          params: { state: stateParam || undefined, limit },
        });
        return (
          data ?? { count: 0, counts: EMPTY_COUNTS, requests: [] }
        );
      } catch (err) {
        if (isMissingRoute(err)) {
          return { count: 0, counts: EMPTY_COUNTS, requests: [] };
        }
        throw err;
      }
    },
  });
}

export function useApprovalDetail(id: string | null | undefined) {
  const enabled = useConnectorEnabled("lighthouse") && !!id;
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "approvals", "detail", id],
    staleTime: STALE_MS,
    queryFn: async (): Promise<ApprovalDetailResponse | null> => {
      try {
        return await edrGet<ApprovalDetailResponse>(`${BASE}/${id}`);
      } catch (err) {
        if (isMissingRoute(err)) return null;
        throw err;
      }
    },
  });
}

export function useApprovalHistory(agentId: string | null | undefined) {
  const enabled = useConnectorEnabled("lighthouse") && !!agentId;
  return useQuery({
    enabled,
    queryKey: ["lighthouse", "approvals", "history", agentId],
    staleTime: STALE_MS,
    queryFn: async (): Promise<ApprovalHistoryResponse> => {
      try {
        const data = await edrGet<ApprovalHistoryResponse>(`${BASE}/history`, {
          params: { agentId: agentId ?? undefined },
        });
        return data ?? { agentId: agentId!, count: 0, requests: [] };
      } catch (err) {
        if (isMissingRoute(err)) {
          return { agentId: agentId!, count: 0, requests: [] };
        }
        throw err;
      }
    },
  });
}

// ---------------- Mutations ----------------

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["lighthouse", "approvals"] });
}

interface ApproveVars {
  id: string;
  reason?: string;
  conditions?: ApprovalConditions;
}

export function useApproveApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: ApproveVars) =>
      edrPost(`${BASE}/${vars.id}/approve`, {
        reason: vars.reason,
        conditions: vars.conditions ?? {},
      }),
    onSuccess: () => {
      toast.success("Approved");
      invalidateAll(qc);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Failed to approve"),
  });
}

interface DenyVars {
  id: string;
  reason?: string;
}

export function useDenyApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: DenyVars) =>
      edrPost(`${BASE}/${vars.id}/deny`, { reason: vars.reason }),
    onSuccess: () => {
      toast.success("Denied");
      invalidateAll(qc);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Failed to deny"),
  });
}

interface RequestInfoVars {
  id: string;
  reason: string;
}

export function useRequestInfoApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RequestInfoVars) =>
      edrPost(`${BASE}/${vars.id}/request-info`, { reason: vars.reason }),
    onSuccess: () => {
      toast.success("Sent");
      invalidateAll(qc);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Failed to send"),
  });
}

interface PostCommentVars {
  id: string;
  body: string;
  kind?: "comment" | "info_response";
}

export function usePostApprovalComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: PostCommentVars) =>
      edrPost(`${BASE}/${vars.id}/comments`, {
        body: vars.body,
        kind: vars.kind ?? "comment",
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: ["lighthouse", "approvals", "detail", vars.id],
      });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Failed to comment"),
  });
}

interface CreateApprovalVars {
  agentId: string;
  deviceId?: string;
  publisher?: string;
  requestReason: string;
}

export function useCreateApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CreateApprovalVars) =>
      edrPost(BASE, vars),
    onSuccess: () => {
      toast.success("Approval requested");
      invalidateAll(qc);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Failed to request approval"),
  });
}

export function useRunApprovalSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => edrPost<ApprovalSweepResponse>(`${BASE}/sweep`, {}),
    onSuccess: (res) => {
      const r = res as ApprovalSweepResponse | undefined;
      const stale = r?.staleCleaned ?? 0;
      const pending = r?.newlyPending ?? 0;
      const expired = r?.expired ?? 0;
      toast.success(
        `Sweep complete · ${stale} stale cleaned · ${pending} new pending · ${expired} expired`,
      );
      invalidateAll(qc);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Sweep failed"),
  });
}
